
import { getBarcode } from '@/utils/api'
import { BARCODE_SCAN, QRCODE_BARCODE_TYPE_REGEX } from '@/utils/constants'
import errorHandler from '@/utils/errorHandler'
import { displayTargetAlert, openMessage, promptVideoPermission } from '@/utils/helpers'
import { BrowserMultiFormatReader, Result } from '@zxing/library'
import { Barcode, BarcodeCapture } from 'scandit-web-datacapture-barcode'
import { mixins, Options } from 'vue-class-component'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import BarcodeReaderMixin from './BarcodeReaderMixin.vue'
import SoundMixin from './SoundMixin.vue'
import { Watch } from 'vue-property-decorator'
import { isEmpty } from '@/utils/typeChecker'
import { IAPIErrorCode, TError } from '@/utils/types/commonTypes'
import { IBarcode } from '@/utils/types/barcodeTypes'
import { IBarcodeDefinitionType } from '@/utils/types/fieldDefinition'
import { AVAILABLE_DELIMITERS, EBCRType } from '@/utils/enums'

@Options({
  components: {},
  name: 'MultipleBarcodeScanMixin',
})
export default class MultipleBarcodeScanMixin extends mixins(BarcodeReaderMixin, SoundMixin) {
  scannedBarcodeList: IBarcode[] = []
  isAllowToLeave = false
  scannedBarcode = ''
  video: HTMLVideoElement | null = null
  _barcodeDataCapture: BarcodeCapture | undefined
  barcodes = [] as IBarcode[]
  valueCode = ''
  multipleScan = false
  isHandleMultipleScan = false
  isLoadingHandleGotScanBarcode = false

  showMultipleScan = false
  isForcedHideMultipleScan = false
  delimiterValue = AVAILABLE_DELIMITERS[0]

  get isShowMultipleScan() {
    this.showMultipleScan =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.isEnable ?? false
    return this.showMultipleScan
  }

  get delimiter() {
    this.delimiterValue =
      this.$store.state.project?.details?.cameraFeatures?.bulkCameraScanResultSetting?.delimiter ??
      AVAILABLE_DELIMITERS[0]
    return this.delimiterValue
  }

  codeReader: BrowserMultiFormatReader | null = new BrowserMultiFormatReader(
    undefined,
    BARCODE_SCAN.timeBetweenScansMillis as number
  )

  pictureWidth = 320
  pictureHeight = 320

  defaultScanReader(result: Result, codeReader: BrowserMultiFormatReader) {
    this.codeReader = codeReader
    if (!result) return

    const temp: { [key: string]: unknown } = { ...result }
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((temp.text as string) || '')
      return
    }
    this.handleGotScanBarcode((temp.text as string) || '')
  }

  scanditDataResult(symbology: Barcode) {
    if (!symbology) {
      // TODO: Do something when scanResult unavailable
    }
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan((symbology?.data as string) || '')
      return
    }
    this.handleGotScanBarcode((symbology?.data as string) || '')
  }

  async barcodeDetectorPolyFillScanReader(result: DetectedBarcode[]) {
    this.isReadyToScan = true
    if (result.length < 1) return
    const firstDetectQrcode = result[0]?.rawValue || ''
    if (!this.isForcedHideMultipleScan && this.isShowMultipleScan && this.isMultipleScan) {
      this.handleMultipleScan(firstDetectQrcode)
      return
    }
    result.forEach((barcode) => {
      this.handleGotScanBarcode(barcode.rawValue)
    })
  }

  async handleMultipleScan(plainText: string) {
    if (this.isHandleMultipleScan) {
      return
    }
    this.isHandleMultipleScan = true
    this.$confirm(this.$t('confirm_scanned_value', { item: plainText }), this.$t('info'), {
      confirmButtonText: this.$t('confirm'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
      closeOnClickModal: true,
      showCancelButton: true,
    })
      .then(() => {
        this.valueCode = this.processValueCode(plainText)
      })
      .finally(() => {
        this.isHandleMultipleScan = false
      })
  }

  async setupVideo() {
    if (!this.video) {
      this.video = document.getElementById('video') as HTMLVideoElement
    } else {
      this.video.srcObject = null
    }
    if (this.selectedCamera) {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            aspectRatio: 1,
            deviceId: { exact: this.selectedCamera },
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          console.log('error', e)
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    } else {
      await navigator.mediaDevices
        .getUserMedia({
          video: {
            facingMode: 'environment',
            aspectRatio: 1,
          },
        })
        .then(async (stream) => {
          await this.setStream(stream)
        })
        .catch((e) => {
          errorHandler(e.name === 'NotAllowedError' ? 'camera_access_not_allow' : e)
        })
    }
  }

  async mounted() {
    await this.initCameraSelection()
    this.setupCameraSetting()
  }

  async setupCameraSetting() {
    try {
      this.isReadyToScan = false
      switch (this.barcodeReaderType as EBCRType) {
        case EBCRType.SCANDIT:
          await this.initScanditReader()
          break
        case EBCRType.BARCODE_DETECTION:
          await this.initBarcodeReaderPolyFill()
          break
        default:
          await promptVideoPermission()
          await this.initZxingReader()
          break
      }
    } catch (error) {
      errorHandler(error as TError)
    } finally {
      this.isReadyToScan = true
    }
  }

  openErrorToast(message: string) {
    openMessage(message, 'error', 0)
  }

  get currentBarcodeId(): string {
    return this.$store.state.barcode?.barcode?.id
  }

  validateBarcode(resultScanned: IBarcode, scannedBarcodeList?: IBarcode[]) {
    throw Error('This function need to implement on component ' + resultScanned)
  }

  async handleGotScanBarcode(plainText: string) {
    try {
      const regexBarcodeId = QRCODE_BARCODE_TYPE_REGEX
      let barcodeId = ''
      let externalId
      if (plainText.match(regexBarcodeId)) {
        const urlParts = plainText.split('/')
        barcodeId = urlParts.pop() || ''
        // Scanned barcode is current barcode, skip it
        if (barcodeId === this.currentBarcodeId) {
          openMessage('barcode_already_scanned', 'info')
          return
        }

        // Skip barcode which one already added in list
        if (this.scannedBarcodeList.find((val) => val.id === barcodeId)) {
          openMessage(this.$t('barcode_already_scanned', { id: barcodeId }), 'info')
          return
        }
      } else {
        const detectedText = plainText || ''
        const projectCode = this.$route?.params?.project.split('@')
        if (projectCode.length > 1) projectCode.pop()

        barcodeId = `projectCode=${projectCode[0]}`
        externalId = detectedText
      }
      this.isLoadingHandleGotScanBarcode = true
      const resultScanned: IBarcode = await getBarcode(barcodeId, externalId)
      if (!resultScanned) throw this.$t('barcode does not exist')
      if (
        this.bulkUpdateOperationLimitCount &&
        this.scannedBarcodeList.length >= this.bulkUpdateOperationLimitCount.maxValue
      ) {
        throw this.$t('bulk_tracking_over_barcodes_is_not_allowed', {
          count: this.bulkUpdateOperationLimitCount.maxValue,
        })
      }

      await this.validateBarcode(resultScanned, this.scannedBarcodeList)

      // Display target barcode notifications if any.
      await displayTargetAlert(resultScanned)

      this.scannedBarcode = resultScanned.id
      if (typeof this.addBarcode === 'function') {
        this.addBarcode(resultScanned.id)
      }
      if (!this.scannedBarcodeList.find((bc) => bc.id === resultScanned?.id)) {
        this.scannedBarcodeList.push(resultScanned)
        this.onNewScannedBarcode(resultScanned)

        openMessage(this.$t('barcode detected'), 'success')

        this.valueCode = ''
      }
    } catch (error) {
      this.handleScanError(error)
    } finally {
      this.isLoadingHandleGotScanBarcode = false
      this.valueCode = ''
    }
  }

  handleScanError(error: unknown) {
    this.playFailSound()
    if (error === 'cancel') return
    let message

    // const error = err as TError
    if (typeof error === 'object' && error?.hasOwnProperty('id')) {
      message = this.$t(`errors.${(error as IAPIErrorCode)?.id?.pop()}`)
    } else if (typeof error === 'string') {
      const modelObject = this.errorVariableTemplate()
      errorHandler(error as TError, modelObject)
      return
    } else if (!isEmpty(error)) {
      message = JSON.stringify(error)
    }
    if (message && message.trim() !== '') {
      this.openErrorToast(message)
    }
  }

  // We will implement error variable template according action here
  errorVariableTemplate() {
    return {
      barcodeType: this.barcodeName,
      fTP1: this.currentTrackPointName,
      tTP2: this.nextSelectTrackingPointName,
    }
  }

  get project() {
    return this.$store.state.project.details
  }

  get barcodeType(): IBarcodeDefinitionType {
    return this.project?.barcodeTypes?.[this.barcode?.barcodeType]
  }

  get barcodeName(): string {
    return this.barcodeType?.name || ''
  }

  get currentTrackPointName() {
    return this.project?.trackPoints[this.currentTrackPointKey]?.name
  }

  get currentTrackPointKey() {
    return this.barcode?.currentTrackPointKey
  }

  onNewScannedBarcode(bc: IBarcode) {
    const found = this.barcodes.find((barcode) => barcode.id === bc.id)

    this.playSuccessSound()

    if (!found && bc?.id !== this.barcode?.id) this.barcodes.push(bc)
  }

  async onLeaveGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (this.isAllowToLeave) {
      next()
      return
    }
    await this.$confirm(this.$t('not_save_confirm'), '', {
      confirmButtonText: this.$t('ok'),
      confirmButtonClass: 'danger',
      cancelButtonText: this.$t('cancel'),
    })
      .then(async () => {
        next()
      })
      .catch((e: TError) => {
        if (e !== 'cancel') {
          next()
        }
      })
    next(false)
  }

  submit() {
    this.isAllowToLeave = true
    this.forward()
  }

  set isMultipleScan(value: boolean) {
    localStorage.setItem('[multiple-scan]_is_multiple_scan', value.toString())
    this.multipleScan = value
  }

  get isMultipleScan() {
    const storedValue = localStorage.getItem('[multiple-scan]_is_multiple_scan')
    if (!this.isShowMultipleScan) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', 'false')
      return false
    }

    if (!storedValue) {
      localStorage.setItem('[multiple-scan]_is_multiple_scan', this.multipleScan.toString())
      return this.multipleScan
    }

    this.multipleScan = storedValue.toLowerCase() === 'true'
    return this.multipleScan
  }

  handleUpdateValueCode(value: string) {
    this.valueCode = value
  }

  processValueCode(value: string) {
    let result = ''
    if (this.valueCode.trim() !== '') {
      result = this.valueCode + this.delimiter + value
    } else {
      result = value
    }
    return result.trim()
  }

  @Watch('isMultipleScan')
  onMultipleScanModeChange() {
    if (this.isMultipleScan === false && this.valueCode !== '') {
      this.$confirm(this.$t('change_camera_mode_confirm'), this.$t('Warning'), {
        confirmButtonText: this.$t('confirm'),
        confirmButtonClass: 'danger',
        cancelButtonText: this.$t('cancel'),
        closeOnClickModal: true,
        showCancelButton: true,
      })
        .then(() => {
          this.valueCode = ''
        })
        .catch(() => {
          this.isMultipleScan = true
        })
    }
  }
}
